import React, { useEffect, useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import { Paper, CircularProgress, Typography, Box, Button, Alert } from "@mui/material";
import NavBar from "../navbar";
import { useNavigate, useParams } from "react-router-dom";
import config from "../config";
import * as Sentry from "@sentry/react";
import UserRestrictionFields, { toPayload } from "./UserRestrictionFields";

const EditUserRestriction = () => {
    const { restriction_id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + "/admin/endpoint-user-restrictions", {
                    headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
                });
                if (!response.ok) throw new Error(`Error: ${response.status}`);
                const all = await response.json();
                const found = all.find(r => r.id === restriction_id);
                if (!found) throw new Error("Restriction not found");
                setData(found);
                setOriginalData(found);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [restriction_id]);

    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) return validate_logged_in;

    const handleChange = (field, value) => setData({ ...data, [field]: value });

    const handleSave = async () => {
        try {
            setSaving(true); setError(null);
            const response = await fetch(config.apiBaseUrl + "/admin/endpoint-user-restrictions", {
                method: "PUT",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
                body: JSON.stringify({ id: data.id, ...toPayload(data) }),
            });
            if (!response.ok) { const err = await response.json(); throw new Error(err.detail || `Error: ${response.status}`); }
            setSuccess(true);
            const updated = await response.json();
            setData(updated);
            setOriginalData(updated);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            Sentry.captureException(err); setError(err.message);
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete this restriction?")) return;
        try {
            const response = await fetch(config.apiBaseUrl + `/admin/endpoint-user-restrictions?restriction_id=${restriction_id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", "user-authorization": getSession().token },
            });
            if (!response.ok) throw new Error(`Error: ${response.status}`);
            alert("Deleted!"); navigate("/endpoint-user-restrictions");
        } catch (err) { Sentry.captureException(err); alert(`Failed: ${err.message}`); }
    };

    const hasChanges = data && originalData && JSON.stringify(data) !== JSON.stringify(originalData);
    if (loading) return <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress /></div>;
    if (error && !data) return <div style={{ textAlign: "center", padding: "20px", color: "red" }}><p>{error}</p></div>;

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 900, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>Edit User Restriction</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {data.endpoint_path} · {data.user_email}
                    </Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>Updated successfully!</Alert>}
                    <UserRestrictionFields data={data} onChange={handleChange} saving={saving} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !hasChanges}>{saving ? "Saving..." : "Save Changes"}</Button>
                        <Box>
                            <Button variant="outlined" color="secondary" onClick={() => setData(originalData)} disabled={saving || !hasChanges} sx={{ mr: 2 }}>Discard</Button>
                            <Button variant="outlined" color="error" onClick={handleDelete} disabled={saving} sx={{ mr: 2 }}>Delete</Button>
                            <Button variant="outlined" onClick={() => navigate("/endpoint-user-restrictions")} disabled={saving}>Back</Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default EditUserRestriction;
